
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { loginRequest } from "@/store/slices/authSlice";

const loginSchema = z.object({
  user_id: z.string().min(1, "Email or phone number is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated, companySetupComplete } = useAppSelector((state) => state.auth);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      user_id: "",
      password: "",
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      if (!companySetupComplete) {
        navigate("/company-setup");
      } else {
        navigate("/");
      }
    }
  }, [isAuthenticated, companySetupComplete, navigate]);

  useEffect(() => {
    if (error?.error?.detail?.general) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.error.detail.general[0],
      });
    }
  }, [error, toast]);

  const formatPhoneNumber = (value: string) => {
    // Remove any non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Check if it's a phone number (only digits)
    if (/^\d+$/.test(digitsOnly)) {
      // If it already starts with +20, don't add it again
      if (value.startsWith('+2')) {
        return value;
      }
      // Add +20 prefix if it's not there
      return `+2${digitsOnly}`;
    }
    
    // If it's not a phone number (contains non-digits), return as is
    return value;
  };

  const onSubmit = async (data: LoginFormData) => {
    const formattedUserId = formatPhoneNumber(data.user_id);
    dispatch(loginRequest({
      user_id: formattedUserId,
      password: data.password
    }));
  };

  return (
    <div className="container max-w-md mx-auto py-10">
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8">
        <h1 className="text-2xl font-bold mb-8 text-slate-900">Login</h1>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email or Phone Number</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter your email or phone number" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage>
                    {error?.error?.detail?.user_id?.[0]}
                  </FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input 
                      type="password" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage>
                    {error?.error?.detail?.password?.[0]}
                  </FormMessage>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </Button>

            <div className="text-center text-sm text-slate-600">
              Don't have an account?{" "}
              <Button
                variant="link"
                className="p-0 h-auto font-semibold"
                onClick={() => navigate("/register")}
              >
                Register here
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default Login;
